import { FC, useEffect, useMemo, useState } from "react";
import { useHistory, Link as RouterLink } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Stack,
  Alert,
  Divider,
  Button,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { isEmpty } from "lodash";
import { format, parseISO, eachDayOfInterval, isWeekend } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { ControlledTextField } from "@benefit-systems/hr-tools-ui";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import useBasePath from "src/utils/hooks/useBasePath";
import __ from "src/utils/Translations";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import { useFetchPublicHolidays } from "src/Pages/Company/Absences/api/calendars.queries";
import getMonthRange from "src/utils/Company/Absences/getMonthRange";
import countDays from "src/utils/Company/Absences/countDays";
import { formatDateRangePreviewFromDates } from "src/utils/Company/Absences/formatDateRangePreview";
import { prepareCustomFieldsDefaultValues } from "src/utils/Company/Absences/prepareAbsenceRequestDefaultFormValues";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import AbsenceRequestDatepicker from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestDatepicker";
import AbsenceHourPicker from "src/Pages/Company/Absences/AbsenceRequest/AbsenceHourPicker";
import BalanceStatusBadge from "src/Pages/Company/Absences/AbsenceRequest/BalanceStatusBadge";
import EmployeesAutocompletePreview from "src/Pages/Company/Absences/AbsenceRequest/EmployeesAutocomplete/EmployeesAutocompletePreview";
import BalanceStatusBadgePreview from "src/Pages/Company/Absences/AbsenceRequest/BalanceStatusBadge/BalanceStatusBadgePreview";
import AbsenceRequestAttachments from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestAttachments";
import AbsenceRequestCustomFields from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequestForm/AbsenceRequestCustomFields";
import { IAbsenceRequestFormValues } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import {
  EPatternUnit,
  IAbsenceType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { IExcludedDay } from "src/Pages/Company/Absences/Calendars/Calendars.models";

interface IAbsenceRequestFormPreviewProps {
  absenceType: IAbsenceType;
}

const AbsenceRequestFormPreview: FC<IAbsenceRequestFormPreviewProps> = ({
  absenceType,
}) => {
  const basePath = useBasePath();
  const history = useHistory();
  const companyId = useCompanyId();
  const logStatus = useLogStatus();

  const initialDate = new Date();

  const currentYear = initialDate.getFullYear().toString();

  const [displayMonths, setDisplayMonths] = useState(
    getMonthRange(initialDate)
  );
  const [hoursCount, setHoursCount] = useState(0);

  if (!companyId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No company ID provided.",
      file: "AbsenceRequestFormPreview.tsx",
    });
  }

  if (!absenceType) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Type data provided.",
      file: "AbsenceRequestFormPreview.tsx",
    });
  }

  const formMethods = useForm<IAbsenceRequestFormValues>({
    mode: "onChange",
    defaultValues: {
      unit: EPatternUnit.Day,
      dates: [null, null],
      startTime: null,
      endTime: null,
      ...prepareCustomFieldsDefaultValues(
        absenceType.customFieldsConfiguration?.customFields
      ),
    },
  });

  const { control, watch } = formMethods;

  const chosenUnit = watch("unit");
  const chosenDateRange = watch("dates");

  const { data: publicHolidaysData } = useFetchPublicHolidays(
    displayMonths.from,
    displayMonths.to
  );

  const excludedDays = useMemo(() => {
    const weekends: IExcludedDay[] = eachDayOfInterval({
      start: parseISO(displayMonths.from),
      end: parseISO(displayMonths.to),
    })
      .filter(isWeekend)
      .map((date) => ({ date: format(date, "yyyy-MM-dd") }));

    const holidays: IExcludedDay[] = publicHolidaysData ?? [];

    const allDates = new Set(weekends.map((d) => d.date));
    const merged = [
      ...weekends,
      ...holidays.filter((h) => !allDates.has(h.date)),
    ];

    return merged;
  }, [displayMonths, publicHolidaysData]);

  const hasValidDateRange = useMemo(
    () => chosenDateRange && chosenDateRange.some((date) => date !== null),
    [chosenDateRange]
  );

  const dayCount = useMemo(() => {
    if (chosenUnit === EPatternUnit.Day) {
      return countDays(
        chosenDateRange,
        absenceType.configurationOptions.billInCalendarDays,
        excludedDays
      );
    }
    if (chosenUnit === EPatternUnit.Hour) {
      if (!hoursCount) return 0;

      return Math.ceil(hoursCount / 8);
    }

    return 0;
  }, [
    absenceType.configurationOptions.billInCalendarDays,
    chosenDateRange,
    chosenUnit,
    excludedDays,
    hoursCount,
  ]);

  useEffect(() => {
    if (chosenUnit === EPatternUnit.Day) {
      let calculatedHoursCount = 0;

      calculatedHoursCount = dayCount * 8;
      setHoursCount(calculatedHoursCount);
    }
  }, [chosenDateRange, chosenUnit, dayCount]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={pl}>
      <FormProvider {...formMethods}>
        <form noValidate>
          {absenceType.description && (
            <Fieldset legend={__("Opis")}>
              <Typography variant="body1">{absenceType.description}</Typography>
            </Fieldset>
          )}
          <Fieldset
            legend={__("Termin wniosku")}
            helpText={
              absenceType.configurationOptions.billInHours
                ? __(
                    "Wniosek godzinny można składać wyłącznie na jeden dzień pracy. Jeżeli chcesz złożyć wniosek na kilka dni, wybierz wniosek dzienny."
                  )
                : undefined
            }
          >
            <Controller
              name="unit"
              control={control}
              render={({ field: { value, onChange, ref } }) => (
                <FormControl>
                  <RadioGroup
                    value={value}
                    onChange={(event) => {
                      onChange(event.target.value);
                      setHoursCount(0);
                    }}
                    ref={ref}
                    data-testid="absence-request-form-field__unit"
                  >
                    <FormControlLabel
                      value={EPatternUnit.Day}
                      control={<Radio />}
                      label={__("Dzienny")}
                    />
                    {absenceType.configurationOptions.billInHours && (
                      <FormControlLabel
                        value={EPatternUnit.Hour}
                        control={<Radio />}
                        label={__("Godzinowy")}
                      />
                    )}
                  </RadioGroup>
                </FormControl>
              )}
            />
            {absenceType && (
              <Box sx={{ bgcolor: "grey.100", padding: "1rem 2rem 1.5rem" }}>
                <Stack
                  direction="row"
                  spacing={4}
                  sx={{ alignItems: "center" }}
                >
                  {chosenUnit === EPatternUnit.Day && hasValidDateRange && (
                    <Box>
                      <Typography component="span" variant="subtitle1">
                        {__("Termin")}:{" "}
                      </Typography>
                      <Typography component="span" variant="body1">
                        {formatDateRangePreviewFromDates(chosenDateRange)}
                      </Typography>
                    </Box>
                  )}
                  <BalanceStatusBadge
                    labelUT="Wybrane"
                    days={dayCount}
                    hours={hoursCount}
                    color="primary.dark"
                  />
                </Stack>

                <Divider />

                <BalanceStatusBadgePreview
                  labelUT="Dostępne"
                  color="primary.dark"
                />

                {chosenUnit === EPatternUnit.Day && (
                  <AbsenceRequestDatepicker
                    absenceRequestDays={[]}
                    excludedDays={excludedDays}
                    plannedDays={[]}
                    multipleRequestsPerDayAllowed={
                      absenceType.configurationOptions
                        .multipleRequestsPerDayAllowed
                    }
                    billInCalendarDays={
                      absenceType.configurationOptions.billInCalendarDays
                    }
                    submitLimitationFrom={absenceType.submitLimitationFrom}
                    submitLimitationTo={absenceType.submitLimitationTo}
                    loading={false}
                    handleMonthChange={(month: Date) =>
                      setDisplayMonths(getMonthRange(month))
                    }
                    disableMonthChange
                    yearsWithCalendar={[currentYear]}
                    displayMonths={displayMonths}
                  />
                )}
                {chosenUnit === EPatternUnit.Hour && (
                  <AbsenceHourPicker
                    absenceRequestDays={[]}
                    excludedDays={excludedDays}
                    plannedDays={[]}
                    multipleRequestsPerDayAllowed={
                      absenceType.configurationOptions
                        .multipleRequestsPerDayAllowed
                    }
                    billInCalendarDays={
                      absenceType.configurationOptions.billInCalendarDays
                    }
                    submitLimitationFrom={absenceType.submitLimitationFrom}
                    submitLimitationTo={absenceType.submitLimitationTo}
                    hoursCount={hoursCount}
                    setHoursCount={setHoursCount}
                    handleMonthChange={(month: Date) =>
                      setDisplayMonths(getMonthRange(month))
                    }
                  />
                )}

                {absenceType.configurationOptions.billInHours && (
                  <Alert severity="info" sx={{ mt: "1rem" }}>
                    {__(
                      "Pierwszy wniosek złożony w danym miesiącu lub roku ustala sposób składania kolejnych. Jeśli wniosek został złożony w trybie dziennym, ta forma będzie obowiązywać dla następnych wniosków w tym okresie."
                    )}
                  </Alert>
                )}
              </Box>
            )}
          </Fieldset>
          <Fieldset legend={__("Uprawnienia i zastępstwo:")}>
            <Typography variant="body2" sx={{ mb: "0.5rem" }}>
              {__("Imię i nazwisko osoby akceptującej wniosek")}:
            </Typography>
            <EmployeesAutocompletePreview
              name="approver"
              label={__("Imię i nazwisko")}
              approvers={absenceType.approvers}
              required={!!absenceType.approvers.length}
              disabled={!absenceType.approvers.length}
            />
            {absenceType.configurationOptions.replacerConfiguration.enabled && (
              <>
                <Typography variant="body2" sx={{ m: "1rem 0 0.5rem" }}>
                  {__("Imię i nazwisko osoby wyznaczonej na zastępstwo:")}
                </Typography>
                <EmployeesAutocompletePreview
                  name="replacer"
                  label={__("Imię i nazwisko")}
                  required={
                    absenceType.configurationOptions.replacerConfiguration
                      .required
                  }
                  disabled={false}
                />
              </>
            )}
          </Fieldset>
          {absenceType.configurationOptions.justificationRequired && (
            <Fieldset legend={__("Niezbędne informacje:")}>
              <ControlledTextField
                name="justification"
                label={__("Uzasadnienie nieobecności")}
                multiline
                maxRows={3}
                maxLength={255}
                required
                data-testid="absence-request-form-field__justification"
              />
            </Fieldset>
          )}
          {!isEmpty(absenceType.customFieldsConfiguration?.customFields) && (
            <AbsenceRequestCustomFields
              customFields={
                absenceType.customFieldsConfiguration?.customFields || []
              }
            />
          )}
          {absenceType.configurationOptions.attachmentConfiguration.enabled && (
            <Fieldset
              legend={__("Załączniki")}
              required={
                absenceType.configurationOptions.attachmentConfiguration
                  .required
              }
            >
              <AbsenceRequestAttachments attachments={[]} />
            </Fieldset>
          )}

          {absenceType.configurationOptions.commentsAllowed && (
            <Fieldset legend={__("Dodaj komentarz:")} divider={false}>
              <Typography variant="body2" sx={{ mb: "0.5rem" }}>
                {__("Dodawanie komentarza jest opcjonalne.")}
              </Typography>
              <ControlledTextField
                name="comment"
                label={__("Treść komentarza")}
                multiline
                maxRows={3}
                maxLength={255}
                data-testid="absence-request-form-field__comment"
              />
            </Fieldset>
          )}
          <Stack
            direction="row"
            sx={{ justifyContent: "flex-end", mt: "2rem" }}
            spacing={2}
          >
            <Button
              variant="outlined"
              size="large"
              onClick={() => history.goBack()}
            >
              {__("Anuluj")}
            </Button>
            <Button
              component={RouterLink}
              variant="contained"
              size="large"
              to={`${basePath}/absences/absence-type/${absenceType.id}/edit`}
              data-testid="absence-request-form__edit-button"
            >
              {__("Edytuj szablon")}
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </LocalizationProvider>
  );
};

export default AbsenceRequestFormPreview;
